import RadioButtonGroup from 'components/elements/form/RadioButtonGroup'
import CheckBoxGroup from 'components/elements/form/CheckBoxGroup'
import TextBoxGroup from 'components/elements/form/TextBoxGroup'
import CallModal from 'components/elements/modal/CallModal'
import InformationModal from 'components/elements/modal/InformationModal'
import RequestTemplate from 'components/templates/RequestTemplate'
import { DEFAULT_FORWARD_BUTTON_LABEL } from 'constants/label'
import { ERROR } from 'constants/messages'
import { PAGE_PATH } from 'constants/pagePath'
import { VEHICLE_STATE, VEHICLE_STATE_FORM } from 'constants/qnState'
import React, { useCallback, useEffect } from 'react'
import {
  FuelTypes,
  QnStatesProps,
  HighwayLocationTypes,
  LocationTypes,
  StorageLocationTypes,
  LossKeyStatusTypes,
} from 'states/QnState'
import { StatesProps, useEditLocationState } from 'utils/commonHooks'
import { containsIncompatibleChar, createOptions } from 'utils/commonUtils'
import { success } from 'utils/status'
import { useProgressPoint } from '../../../utils/commonHooks'

const locationTypeOptions = createOptions(
  VEHICLE_STATE_FORM.locationType.options
)

const highwayLocationOptions = createOptions(
  VEHICLE_STATE_FORM.highwayLocation.options
)

const homeStorageLocationOptions = createOptions(
  VEHICLE_STATE_FORM.homeStorageLocation.options
)

const otherStorageLocationOptions = createOptions(
  VEHICLE_STATE_FORM.otherStorageLocation.options
)

const SUPPLEMENT_MAX_LENGTH = 50

const closeLabel = 'OK'

const supplementSuffix = 'を入力（50文字まで入力可能）'

const emptyFuelMessage =
  '自宅・通常保管場所での燃料切れは\nロードアシスタンス対象外です。'

const lossKeyMessage =
  '自宅・通常保管場所でのカギの紛失は\nロードアシスタンス対象外です。'

const highwayModalMessage =
  '本線上の詳細を確認するため、\nオペレーターにお繋ぎします。'

/**
 * QN-05: 車の周辺状況
 */
const Surrounding: React.FC<StatesProps> = (props: QnStatesProps) => {
  const { state, setState } = props
  const [
    editState,
    setEditingItem,
    setLocationStateInfo,
  ] = useEditLocationState()
  const {
    locationType,
    localLoadSupplement,
    highwayLocation,
    highwaySupplement,
    homeStorageLocation,
    homeStorageSupplement,
    otherStorageLocation,
    otherStorageSupplement,
    noPavedLoadSupplement,
    otherSupplement,
  } = editState.locationStateInfo

  /** 対象外ポップアップのコンテンツ */
  const CautionMessage = React.useMemo(() => {
    return (
      (editState.vehicleState?.includes(VEHICLE_STATE.EMPTY_FUEL)
        ? emptyFuelMessage
        : lossKeyMessage) ?? ''
    )
  }, [editState.vehicleState])

  // 車のトラブル画面(燃料切れ、カギのトラブル画面)で、車の周辺状況画面で対象外となる選択をした場合に、対象外ポップアップを表示するフラグ
  const [hasExcludeTerm, setHasExcludeTerm] = React.useState(false)
  // 高速道路本線上選択時に表示する対象外ポップアップ表示のフラグ
  const [hasExcludeHighway, setHasExcludeHighway] = React.useState<
    boolean | undefined
  >()

  const errorMessage = React.useMemo(() => {
    // LocationTypeごとの補足の入力内容チェック
    switch (locationType) {
      case LocationTypes.localLoad:
        if (containsIncompatibleChar(localLoadSupplement)) {
          return ERROR.CONTAINS_SURROGATE_PAIR
        }
        break
      case LocationTypes.highway:
        if (containsIncompatibleChar(highwaySupplement)) {
          return ERROR.CONTAINS_SURROGATE_PAIR
        }
        break
      case LocationTypes.home:
        if (containsIncompatibleChar(homeStorageSupplement)) {
          return ERROR.CONTAINS_SURROGATE_PAIR
        }
        break
      case LocationTypes.garageParking:
        if (containsIncompatibleChar(otherStorageSupplement)) {
          return ERROR.CONTAINS_SURROGATE_PAIR
        }
        break
      case LocationTypes.noPavedLoad:
        if (containsIncompatibleChar(noPavedLoadSupplement)) {
          return ERROR.CONTAINS_SURROGATE_PAIR
        }
        break
      case LocationTypes.other:
        if (containsIncompatibleChar(otherSupplement)) {
          return ERROR.CONTAINS_SURROGATE_PAIR
        }
        break
    }
    return ''
  }, [editState.locationStateInfo])

  const isDisabled = React.useMemo(() => {
    if (errorMessage.length > 0) {
      return true
    }
    if (locationType === LocationTypes.highway && highwayLocation === '') {
      return true
    }
  }, [errorMessage, locationType, highwayLocation])

  const forwardTo = React.useMemo(() => {
    return state.confirmationChangeType
      ? PAGE_PATH.CONFIRMATION
      : PAGE_PATH.VEHICLE_STATE
  }, [])

  const backTo = React.useMemo(() => {
    return state.confirmationChangeType
      ? PAGE_PATH.CONFIRMATION
      : PAGE_PATH.TROUBLE_PLACE
  }, [state.confirmationChangeType])

  /** 場所の種別変更時のコールバック */
  const handleLocationTypeChanged = useCallback(
    (value: LocationTypes) => {
      setLocationStateInfo({
        locationType: value,
      })

      /** 車のトラブル画面で選択した内容によって車の周辺状況画面で対象外ポップアップを表示対象となる条件 */
      const isExcludedPopupFlag: boolean =
        ((editState.vehicleState?.includes(VEHICLE_STATE.LOSS_KEY) &&
          editState.vehicleStateInfo.lossKeyStatus ===
            LossKeyStatusTypes.loss) ||
          (editState.vehicleState?.includes(VEHICLE_STATE.EMPTY_FUEL) &&
            !editState.vehicleStateInfo.fuelType.includes(
              FuelTypes.electricity
            ))) ??
        false
      if (value === LocationTypes.home && isExcludedPopupFlag) {
        setHasExcludeTerm(true)
      }

      if (value === LocationTypes.highway) {
        setHasExcludeHighway(true)
      }
    },
    [setLocationStateInfo]
  )

  /** 高速道路の停車場所変更時のコールバック */
  const handleHighwayLocationChanged = useCallback(
    (value: HighwayLocationTypes) => {
      setLocationStateInfo({
        highwayLocation: value,
      })
      // 停車場所を選択を「本線」にした場合、対象外ポップアップを表示する
      setHasExcludeHighway(value === HighwayLocationTypes.mainLine)
    },
    [setLocationStateInfo]
  )

  /** 自宅保管場所の特徴変更時のコールバック */
  const handleHomeStorageLocationChanged = useCallback(
    (value: StorageLocationTypes[]) => {
      setLocationStateInfo({
        homeStorageLocation: value,
      })
    },
    [setLocationStateInfo]
  )

  /** その他保管場所の特徴変更時のコールバック */
  const handleOtherStorageLocationChanged = useCallback(
    (value: StorageLocationTypes[]) => {
      setLocationStateInfo({
        otherStorageLocation: value,
      })
    },
    [setLocationStateInfo]
  )

  /** 一般道の補足情報変更時のコールバック */
  const handleLocalLoadSupplementChanged = useCallback(
    (value: string) => {
      setLocationStateInfo({
        localLoadSupplement: value,
      })
    },
    [setLocationStateInfo]
  )

  /** 高速道路の補足情報変更時のコールバック */
  const handleHighwaySupplementChanged = useCallback(
    (value: string) => {
      setLocationStateInfo({
        highwaySupplement: value,
      })
    },
    [setLocationStateInfo]
  )

  /** 自宅・通常保管場所の補足情報変更時のコールバック */
  const handleHomeStorageSupplementChanged = useCallback(
    (value: string) => {
      setLocationStateInfo({
        homeStorageSupplement: value,
      })
    },
    [setLocationStateInfo]
  )

  /** その他保管場所の補足情報変更時のコールバック */
  const handleOtherStorageSupplementChanged = useCallback(
    (value: string) => {
      setLocationStateInfo({
        otherStorageSupplement: value,
      })
    },
    [setLocationStateInfo]
  )

  /** 舗装されていない道路の補足情報変更時のコールバック */
  const handleNoPavedLoadSupplementChanged = useCallback(
    (value: string) => {
      setLocationStateInfo({
        noPavedLoadSupplement: value,
      })
    },
    [setLocationStateInfo]
  )

  /** その他の補足情報変更時のコールバック */
  const handleOtherSupplementChanged = useCallback(
    (value: string) => {
      setLocationStateInfo({
        otherSupplement: value,
      })
    },
    [setLocationStateInfo]
  )

  /** サービス対象外モーダルClose時のコールバック */
  const closeExcludeModal = useCallback(() => {
    setHasExcludeTerm(false)
  }, [])

  const forwardAction = React.useCallback(() => {
    setState({
      ...state,
      locationStateInfo: editState.locationStateInfo,
    })
    return success()
  }, [editState.locationStateInfo])

  /** 戻るボタン押下時にeditingItemの中身をqnStateの内容と同期させる */
  const backAction = useCallback(() => {
    setEditingItem({
      ...editState,
      locationStateInfo: state.locationStateInfo,
    })
    return success()
  }, [state.confirmationChangeType, state.locationStateInfo])

  // EditingItemに "初期値" または "すでに選択済みのもの" をセットする
  useEffect(() => {
    setLocationStateInfo({
      locationType:
        locationType === '' ? LocationTypes.localLoad : locationType,
    })
  }, [])

  return (
    <RequestTemplate
      title={'お車の周辺状況'}
      showContact
      progress={useProgressPoint()}
      content={
        <React.Fragment>
          <InformationModal
            on={hasExcludeTerm}
            content={CautionMessage}
            closeLabel={closeLabel}
            greyBack
            scrollLock
            textCenter
            closeFunction={closeExcludeModal}
          />
          {locationType.includes(LocationTypes.highway) &&
            highwayLocation.includes(HighwayLocationTypes.mainLine) && (
              <CallModal isTransitionReAccess>
                <InformationModal
                  on={!!hasExcludeHighway}
                  content={highwayModalMessage}
                  closeLabel={closeLabel}
                  greyBack
                  scrollLock
                  textCenter
                  closeFunction={(): void => setHasExcludeHighway(false)}
                />
              </CallModal>
            )}
          <RadioButtonGroup
            title={`${VEHICLE_STATE_FORM.locationType.formName}を選択`}
            isRequired
            options={locationTypeOptions}
            selectedValue={locationType}
            onValueChanged={handleLocationTypeChanged}
          />
          {
            // 一般道
            locationType.includes(LocationTypes.localLoad) && (
              <TextBoxGroup
                title={`${VEHICLE_STATE_FORM.localLoadSupplement.formName}${supplementSuffix}`}
                placeholder={VEHICLE_STATE_FORM.localLoadSupplement.label}
                maxLength={{ length: SUPPLEMENT_MAX_LENGTH }}
                text={localLoadSupplement}
                onChanged={handleLocalLoadSupplementChanged}
              />
            )
          }
          {
            // 高速道路
            locationType.includes(LocationTypes.highway) && (
              <>
                <RadioButtonGroup
                  title={VEHICLE_STATE_FORM.highwayLocation.formName}
                  isRequired
                  options={highwayLocationOptions}
                  selectedValue={highwayLocation}
                  onValueChanged={handleHighwayLocationChanged}
                />
                <TextBoxGroup
                  title={`${VEHICLE_STATE_FORM.highwaySupplement.formName}${supplementSuffix}`}
                  placeholder={VEHICLE_STATE_FORM.highwaySupplement.label}
                  maxLength={{ length: SUPPLEMENT_MAX_LENGTH }}
                  text={highwaySupplement}
                  onChanged={handleHighwaySupplementChanged}
                />
              </>
            )
          }
          {
            // 自宅・通常保管場所
            locationType.includes(LocationTypes.home) && (
              <>
                <CheckBoxGroup
                  title={`${VEHICLE_STATE_FORM.homeStorageLocation.formName}を選択`}
                  isExclusion
                  options={homeStorageLocationOptions}
                  selectedValues={homeStorageLocation}
                  onValueChanged={handleHomeStorageLocationChanged}
                />
                <TextBoxGroup
                  title={`${VEHICLE_STATE_FORM.homeStorageSupplement.formName}${supplementSuffix}`}
                  placeholder={VEHICLE_STATE_FORM.homeStorageSupplement.label}
                  maxLength={{ length: SUPPLEMENT_MAX_LENGTH }}
                  text={homeStorageSupplement}
                  onChanged={handleHomeStorageSupplementChanged}
                />
              </>
            )
          }
          {
            // 上記以外の車庫・駐車場
            locationType.includes(LocationTypes.garageParking) && (
              <>
                <CheckBoxGroup
                  title={`${VEHICLE_STATE_FORM.otherStorageLocation.formName}を選択`}
                  isExclusion
                  options={otherStorageLocationOptions}
                  selectedValues={otherStorageLocation}
                  onValueChanged={handleOtherStorageLocationChanged}
                />
                <TextBoxGroup
                  title={`${VEHICLE_STATE_FORM.otherStorageSupplement.formName}${supplementSuffix}`}
                  placeholder={VEHICLE_STATE_FORM.otherStorageSupplement.label}
                  maxLength={{ length: SUPPLEMENT_MAX_LENGTH }}
                  text={otherStorageSupplement}
                  onChanged={handleOtherStorageSupplementChanged}
                />
              </>
            )
          }
          {
            // 舗装されていない道路
            locationType.includes(LocationTypes.noPavedLoad) && (
              <TextBoxGroup
                title={`${VEHICLE_STATE_FORM.noPavedLoadSupplement.formName}${supplementSuffix}`}
                placeholder={VEHICLE_STATE_FORM.noPavedLoadSupplement.label}
                maxLength={{ length: SUPPLEMENT_MAX_LENGTH }}
                text={noPavedLoadSupplement}
                onChanged={handleNoPavedLoadSupplementChanged}
              />
            )
          }
          {
            // その他
            locationType.includes(LocationTypes.other) && (
              <TextBoxGroup
                title={`${VEHICLE_STATE_FORM.otherSupplement.formName}${supplementSuffix}`}
                placeholder={VEHICLE_STATE_FORM.otherSupplement.label}
                maxLength={{ length: SUPPLEMENT_MAX_LENGTH }}
                text={otherSupplement}
                onChanged={handleOtherSupplementChanged}
              />
            )
          }
        </React.Fragment>
      }
      error={errorMessage}
      forwardTo={forwardTo}
      showBack
      forwardAction={forwardAction}
      backAction={backAction}
      backTo={backTo}
      isDisabled={isDisabled}
      disabledLabel={DEFAULT_FORWARD_BUTTON_LABEL}
      notClearEditingItem
    />
  )
}
export default Surrounding
